import React from "react"
import { graphql } from "gatsby"
import { H4 } from "@fastly/consistently"
import MDXWrapper from "~templates/mdx"

import Link from "~components/link/link"
import Fiddle from "~components/fiddle/fiddle"
import ColumnsList from "~components/columns-list/columns-list"

import * as styles from "./vcl-subroutine.module.css"

const stateTransitions = {
  vcl_recv: {
    inbound: [{ transitionLabel: "Inbound request" }, { transitionCmd: "restart" }],
    outbound: [
      { transitionCmd: "return(lookup)", destinations: ["hash"] },
      { transitionCmd: "return(pass)", destinations: ["hash"] },
      { transitionCmd: "error", destinations: ["hash"] },
      { transitionCmd: "restart", destinations: ["recv"] },
      { transitionCmd: "return(upgrade)" },
    ],
  },
  vcl_hash: {
    inbound: [
      { origins: ["recv"], transitionCmd: "return(lookup)" },
      { origins: ["recv"], transitionCmd: "return(pass)" },
      { origins: ["recv"], transitionCmd: "error" },
    ],
    outbound: [{ transitionCmd: "return(hash)", destinations: ["hit", "miss", "pass", "error"] }],
  },
  vcl_hit: {
    inbound: [{ origins: ["hash"], transitionCmd: "return(hash)" }],
    outbound: [
      { transitionCmd: "return(deliver)", destinations: ["deliver"] },
      { transitionCmd: "return(pass)", destinations: ["pass"] },
      { transitionCmd: "restart", destinations: ["recv"] },
      { transitionCmd: "error", destinations: ["error"] },
    ],
  },
  vcl_miss: {
    inbound: [{ origins: ["hash"], transitionCmd: "return(hash)" }],
    outbound: [
      { transitionCmd: "return(fetch)", destinations: ["fetch"] },
      { transitionCmd: "return(pass)", destinations: ["pass"] },
      { transitionCmd: "return(deliver_stale)", destinations: ["deliver"] },
      { transitionCmd: "error", destinations: ["error"] },
    ],
  },
  vcl_pass: {
    inbound: [
      { origins: ["hash"], transitionCmd: "return(hash)" },
      { origins: ["hit", "miss"], transitionCmd: "return(pass)" },
    ],
    outbound: [
      { transitionCmd: "return(pass)", destinations: ["fetch"] },
      { transitionCmd: "error", destinations: ["error"] },
    ],
  },
  vcl_fetch: {
    inbound: [
      { origins: ["miss"], transitionCmd: "return(fetch)" },
      { origins: ["pass"], transitionCmd: "return(pass)" },
    ],
    outbound: [
      { transitionCmd: "return(deliver)", destinations: ["deliver"] },
      { transitionCmd: "return(deliver_stale)", destinations: ["deliver"] },
      { transitionCmd: "return(pass)", destinations: ["deliver"] },
      { transitionCmd: "restart", destinations: ["recv"] },
      { transitionCmd: "error", destinations: ["error"] },
    ],
  },
  vcl_error: {
    inbound: [
      { origins: ["hash"], transitionCmd: "return(hash)" },
      { origins: ["hit", "miss", "pass", "fetch"], transitionCmd: "error" },
    ],
    outbound: [
      { transitionCmd: "return(deliver)", destinations: ["deliver"] },
      { transitionCmd: "return(deliver_stale)", destinations: ["deliver"] },
      { transitionCmd: "restart", destinations: ["recv"] },
    ],
  },
  vcl_deliver: {
    inbound: [
      { origins: ["hit", "fetch", "error"], transitionCmd: "return(deliver)" },
      { origins: ["miss", "fetch", "error"], transitionCmd: "return(deliver_stale)" },
      { origins: ["fetch"], transitionCmd: "return(pass)" },
    ],
    outbound: [
      { transitionCmd: "return(deliver)", destinations: ["log"] },
      { transitionCmd: "restart", destinations: ["recv"] },
    ],
  },
  vcl_log: {
    inbound: [{ origins: ["deliver"], transitionCmd: "return(deliver)" }],
    outbound: [{ transitionCmd: "return(deliver)" }],
  },
}

const Template = ({ data, title, scopeName, children }) => {
  return (
    <>
      {children}

      {stateTransitions[title] && (
        <>
          <H4 as="h2">
            <Link className="anchor" to="#state-transitions" id="state-transitions" />
            State transitions
          </H4>

          <div className="tableWrapper">
            <table className={styles.stateTransitions}>
              <tbody>
                <tr>
                  <td className={styles.statesBefore}>
                    <ul className={styles.stateList}>
                      {stateTransitions[title].inbound.map((t, idx) => (
                        <li key={idx}>
                          {!!t.origins &&
                            t.origins.map((o) => (
                              <span key={o} className={styles.stateNode}>
                                <Link to={`/reference/vcl/subroutines/${o}/`}>{o}</Link>
                              </span>
                            ))}
                          <span>
                            {t.transitionCmd ? <code>{t.transitionCmd}</code> : <span>{t.transitionLabel}</span>} ⮕
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className={styles.statesThis}>
                    <span className={styles.subName}>{title}</span>
                  </td>
                  <td>
                    <ul className={styles.stateList}>
                      {stateTransitions[title].outbound.map((t, idx) => (
                        <li key={idx}>
                          <span>
                            ⮕ {t.transitionCmd ? <code>{t.transitionCmd}</code> : <span>{t.transitionLabel}</span>}⮕
                          </span>
                          {!!t.destinations &&
                            t.destinations.map((o) => (
                              <span key={o} className={styles.stateNode}>
                                <Link to={`/reference/vcl/subroutines/${o}/`}>{o}</Link>
                              </span>
                            ))}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            To see this subroutine in the context of the full VCL flow, see{" "}
            <Link to="/guides/full-site-delivery/custom-vcl/developer-guide-using">using VCL</Link>.
          </p>
        </>
      )}

      {!!data.example && (
        <>
          <H4 as="h2">
            <Link className="anchor" to="#example" id="example" />
            Example
          </H4>
          <p>
            The code example{" "}
            <Link to={`https://fiddle.fastly.dev/fiddle/${data.example.implementations.vcl.fiddleID}`}>
              <em>{data.example.title}</em>
            </Link>{" "}
            is a good example of the <code>{title}</code> subroutine in use:
          </p>
          <Fiddle id={data.example.implementations.vcl.fiddleID} defaultSub={title.replace(/^vcl_/, "")} />
        </>
      )}

      {!!data.tokens.nodes.length && !!scopeName && (
        <>
          <H4 as="h2">
            <Link className="anchor" to="#tokens" id="tokens" />
            Tokens available in this subroutine
          </H4>
          <p>
            The following limited-scope VCL functions and variables are available for use in this subroutine (those in{" "}
            <strong>bold</strong> are available <em>only</em> in this subroutine, those available in *all* subroutines
            are not listed):
          </p>
          <ColumnsList ordered={false}>
            {data.tokens.nodes.map((tok) => (
              <li key={tok.path}>
                <Link to={tok.path}>
                  {tok.context.subroutines.length === 1 ? <strong>{tok.context.title}</strong> : tok.context.title}
                </Link>
              </li>
            ))}
          </ColumnsList>
        </>
      )}
    </>
  )
}

export const query = graphql`
  query ($mdxPath: String, $title: String, $scopeName: String) {
    mdx(fileAbsolutePath: { eq: $mdxPath }) {
      body
      fileAbsolutePath
      tableOfContents(maxDepth: 10)
    }
    tokens: allSitePage(
      filter: { pageContext: { tokenType: { regex: "/^(function|variable)$/" }, subroutines: { eq: $scopeName } } }
      sort: { fields: pageContext___title }
    ) {
      nodes {
        context: pageContext {
          title
          tokenType
          subroutines
        }
        path
      }
    }
    example: examplesJson(implementations: { vcl: { tokens: { eq: $title } } }) {
      title
      summary
      implementations {
        vcl {
          fiddleID
        }
      }
    }
  }
`

const Wrapper = (props) =>
  MDXWrapper({
    ...props,
    Template,
    pageContext: { ...props.pageContext, withComments: true },
  })

export default Wrapper
